import { AlertTriangle, FileSpreadsheet, X } from 'lucide-react';
import type { PropertyImportPreview } from '@/services/api/platform.api';

interface ImportPreviewBannerProps {
  preview: PropertyImportPreview;
  filename: string;
  onDismiss: () => void;
}

interface DetailRow {
  label: string;
  value: string;
}

/**
 * Build the list of imported values that DON'T map to a field on the create
 * form (the form only carries name / address / city / country / description).
 * These are surfaced so the admin can copy them into the property editor tabs
 * (Guidelines, Amenities, Contacts…) after the property is created.
 */
function buildExtraDetails(preview: PropertyImportPreview): DetailRow[] {
  const rows: DetailRow[] = [];
  const push = (label: string, value: string | number | boolean | undefined) => {
    if (value === undefined || value === null) return;
    const text = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value).trim();
    if (text === '') return;
    rows.push({ label, value: text });
  };

  push('Legal entity', preview.legalEntityName);
  push('ZIP / postal code', preview.zipCode);
  push('VAT ID', preview.vatId);
  push('Website', preview.website);
  push('Check-in', preview.checkInTime);
  push('Check-out', preview.checkOutTime);
  push('Pet policy', preview.petPolicy);
  push('Children allowed', preview.childrenAllowed);
  push('Minimum guest age', preview.minAgeYears);
  push('Minimum nights', preview.minNights);
  push('Minimum advance (days)', preview.minAdvanceDays);
  push('Total units', preview.totalUnitsText);
  push('Floors', preview.floorsText);
  push('Amenities', preview.amenities?.filter(Boolean).join(', '));
  push('House rules', preview.houseRules);

  const c = preview.contacts;
  if (c) {
    push('On-site contact', c.onSite);
    push('Contact email', c.email);
    push('Contact phone', c.phone);
    push('Housekeeping', c.housekeeping);
    push('Security', c.security);
    push('Emergency', c.emergency);
    push('Maintenance', c.maintenance);
  }

  return rows;
}

export function ImportPreviewBanner({ preview, filename, onDismiss }: ImportPreviewBannerProps) {
  const warnings = preview.warnings?.filter(Boolean) ?? [];
  const extras = buildExtraDetails(preview);
  const raw = preview.raw?.filter((r) => r.question || r.answer) ?? [];

  return (
    <div className="rounded-lg border border-brand-200 bg-brand-50" role="status">
      <div className="flex items-start gap-2 px-3 py-2.5">
        <FileSpreadsheet size={16} className="mt-0.5 shrink-0 text-brand-600" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-brand-800">
            Imported from <span className="break-all font-semibold">{filename}</span>
          </p>
          <p className="mt-0.5 text-xs text-brand-700">
            The fields below were filled in from your onboarding sheet. Review and edit
            anything before creating the property.
          </p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 rounded p-1 text-brand-500 hover:bg-brand-100 hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          aria-label="Dismiss import summary"
        >
          <X size={15} aria-hidden="true" />
        </button>
      </div>

      {warnings.length > 0 && (
        <div className="border-t border-brand-200 px-3 py-2.5">
          <p className="flex items-center gap-1.5 text-xs font-semibold text-amber-800">
            <AlertTriangle size={13} aria-hidden="true" />
            Needs your attention
          </p>
          <ul className="mt-1.5 list-disc space-y-1 pl-5 text-xs text-amber-800">
            {warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {extras.length > 0 && (
        <details className="border-t border-brand-200 px-3 py-2.5">
          <summary className="cursor-pointer text-xs font-semibold text-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
            More details captured ({extras.length}) — set these in the editor after creating
          </summary>
          <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-2">
            {extras.map((row) => (
              <div key={row.label} className="min-w-0">
                <dt className="text-[11px] font-medium uppercase tracking-wide text-brand-600">
                  {row.label}
                </dt>
                <dd className="break-words text-xs text-gray-700">{row.value}</dd>
              </div>
            ))}
          </dl>
        </details>
      )}

      {raw.length > 0 && (
        <details className="border-t border-brand-200 px-3 py-2.5">
          <summary className="cursor-pointer text-xs font-semibold text-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
            All answers from the sheet ({raw.length})
          </summary>
          <dl className="mt-2 space-y-2">
            {raw.map((entry, i) => (
              <div key={i} className="min-w-0">
                <dt className="text-[11px] font-medium text-gray-500">{entry.question}</dt>
                <dd className="break-words text-xs text-gray-800">{entry.answer}</dd>
              </div>
            ))}
          </dl>
        </details>
      )}
    </div>
  );
}
